import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  LinearProgress,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as OkIcon,
  Error as CriticalIcon,
  SwapHoriz as SwitchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import {
  useSwitchCampaigns,
  useCreateSwitchCampaign,
  useAddSwitchGroup,
  useUpdateSwitchGroup,
  useLogSwitchDaily,
  useSwitchGroupLogs,
  useFeedBrands,
  useFeedTypes,
  useDeleteSwitchCampaign,
} from '../hooks/useFeed';
import { useBirdProcurements } from '../hooks/useBirds';

// ── helpers ──────────────────────────────────────────────────────────────────

function blendLabel(
  ratio: { old: number; new: number; phase: string },
  fromFeed: string,
  toFeed: string
) {
  if (ratio.old === 0) return `100% ${toFeed}`;
  return `${ratio.old}% ${fromFeed} / ${ratio.new}% ${toFeed}`;
}

function alertColor(status: string) {
  if (status === 'critical') return '#f44336';
  if (status === 'warn') return '#ff9800';
  return '#4caf50';
}

function alertIcon(status: string) {
  if (status === 'critical') return <CriticalIcon fontSize="small" />;
  if (status === 'warn') return <WarningIcon fontSize="small" />;
  return <OkIcon fontSize="small" />;
}

// ── Group Logs Dialog ────────────────────────────────────────────────────────

function GroupLogsDialog({
  groupId,
  groupName,
  birdCount,
  open,
  onClose,
}: {
  groupId: number;
  groupName: string;
  birdCount: number;
  open: boolean;
  onClose: () => void;
}) {
  const { data: logs = [], isLoading } = useSwitchGroupLogs(groupId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{groupName} — Log History</DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress size={24} />}
        {!isLoading && (logs as any[]).length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No logs recorded yet.
          </Typography>
        )}
        {(logs as any[]).map((log: any) => {
          const layRate = ((log.eggs_laid / birdCount) * 100).toFixed(1);
          return (
            <Box
              key={log.id}
              sx={{
                py: 1.5,
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ minWidth: 90, fontWeight: 600 }}>
                {format(new Date(log.log_date), 'dd MMM')}
              </Typography>
              <Typography variant="body2">{log.eggs_laid} eggs</Typography>
              <Chip
                label={`${layRate}%`}
                size="small"
                sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ff9800' }}
              />
              <Chip
                label={log.droppings_quality}
                size="small"
                color={
                  log.droppings_quality === 'watery'
                    ? 'error'
                    : log.droppings_quality === 'loose'
                      ? 'warning'
                      : 'success'
                }
              />
              {log.feed_refused && <Chip label="Feed refused" size="small" color="error" />}
              {log.notes && (
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {log.notes}
                </Typography>
              )}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Group Card ───────────────────────────────────────────────────────────────

function GroupCard({
  group,
  campaignId: _campaignId,
  fromFeed,
  toFeed,
}: {
  group: any;
  campaignId: number;
  fromFeed: string;
  toFeed: string;
}) {
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editBaselineOpen, setEditBaselineOpen] = useState(false);
  const [logForm, setLogForm] = useState({
    log_date: format(new Date(), 'yyyy-MM-dd'),
    eggs_laid: '',
    droppings_quality: 'normal',
    feed_refused: false,
    notes: '',
  });
  const [baselineForm, setBaselineForm] = useState(group.baseline_lay_rate?.toString() ?? '');

  const logMutation = useLogSwitchDaily();
  const updateGroupMutation = useUpdateSwitchGroup();

  const ratio = group.blend_ratio ?? { old: 75, new: 25, phase: 'Days 1–2' };
  const blendDay = group.blend_day ?? 1;
  const layRate = group.latest_lay_rate;
  const drop = group.lay_rate_drop;
  const alertStatus = group.alert_status ?? 'none';
  const accentColor = alertColor(alertStatus);

  const handleLog = async () => {
    if (!logForm.eggs_laid) return;
    try {
      await logMutation.mutateAsync({
        groupId: group.id,
        data: {
          ...logForm,
          eggs_laid: parseInt(logForm.eggs_laid, 10),
          feed_refused: logForm.feed_refused,
        },
      });
      setLogDialogOpen(false);
      setLogForm({
        log_date: format(new Date(), 'yyyy-MM-dd'),
        eggs_laid: '',
        droppings_quality: 'normal',
        feed_refused: false,
        notes: '',
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveBaseline = async () => {
    await updateGroupMutation.mutateAsync({
      groupId: group.id,
      data: { baseline_lay_rate: parseFloat(baselineForm) || null },
    });
    setEditBaselineOpen(false);
  };

  const progressPct = Math.min(100, (blendDay / 7) * 100);
  const isComplete = ratio.old === 0;

  return (
    <Paper
      sx={{
        p: 2.5,
        border: `1px solid ${accentColor}33`,
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: 4,
          height: '100%',
          bgcolor: accentColor,
          borderRadius: '2px 0 0 2px',
        },
      }}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {group.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {group.bird_count.toLocaleString()} birds · {group.feed_per_day_kg} kg/day
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {alertStatus !== 'none' && (
            <Chip
              size="small"
              icon={alertIcon(alertStatus) as React.ReactElement}
              label={alertStatus === 'critical' ? 'Hold at 50/50' : 'Lay drop >5%'}
              sx={{
                bgcolor: `${accentColor}22`,
                color: accentColor,
                fontWeight: 700,
                fontSize: '0.7rem',
              }}
            />
          )}
          {isComplete && (
            <Chip size="small" label="100% Maridav" color="success" sx={{ fontWeight: 700 }} />
          )}
          {group.status === 'on_hold' && (
            <Chip size="small" label="On Hold" color="warning" sx={{ fontWeight: 700 }} />
          )}
        </Box>
      </Box>

      {/* Blend day progress */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Day {blendDay} · {ratio.phase}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#ff9800' }}>
            {blendLabel(ratio, fromFeed, toFeed)}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'rgba(255,255,255,0.1)',
            '& .MuiLinearProgress-bar': {
              bgcolor: isComplete ? '#4caf50' : '#ff9800',
              borderRadius: 4,
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {fromFeed}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {toFeed}
          </Typography>
        </Box>
      </Box>

      {/* Lay rate */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Baseline
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#aaa' }}>
              {group.baseline_lay_rate != null ? `${group.baseline_lay_rate}%` : '—'}
            </Typography>
            <Tooltip title="Set baseline">
              <IconButton
                size="small"
                onClick={() => {
                  setBaselineForm(group.baseline_lay_rate?.toString() ?? '');
                  setEditBaselineOpen(true);
                }}
              >
                <EditIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.3)' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Latest
          </Typography>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ color: layRate != null ? accentColor : '#aaa' }}
          >
            {layRate != null ? `${layRate}%` : '—'}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Change
          </Typography>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ color: drop != null ? (drop > 0 ? '#f44336' : '#4caf50') : '#aaa' }}
          >
            {drop != null ? `${drop > 0 ? '-' : '+'}${Math.abs(drop).toFixed(1)}%` : '—'}
          </Typography>
        </Box>
      </Box>

      {/* On hold reason */}
      {group.status === 'on_hold' && group.hold_reason && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}>
          {group.hold_reason}
        </Alert>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => setLogDialogOpen(true)}
          disabled={group.has_logged_today || isComplete}
          sx={{
            flex: 1,
            background: group.has_logged_today
              ? undefined
              : 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            fontSize: '0.75rem',
          }}
        >
          {group.has_logged_today ? 'Logged Today ✓' : 'Log Today'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setHistoryOpen(true)}
          sx={{ fontSize: '0.75rem', borderColor: 'rgba(255,255,255,0.2)' }}
        >
          History
        </Button>
      </Box>

      {/* Log Dialog */}
      <Dialog open={logDialogOpen} onClose={() => setLogDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Log Daily Observation — {group.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
            <TextField
              label="Date"
              type="date"
              value={logForm.log_date}
              onChange={(e) => setLogForm((f) => ({ ...f, log_date: e.target.value }))}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: group.switch_start_date },
              }}
              fullWidth
              helperText={`Not before switch start (${group.switch_start_date})`}
            />
            <TextField
              label="Eggs Laid"
              type="number"
              value={logForm.eggs_laid}
              onChange={(e) => setLogForm((f) => ({ ...f, eggs_laid: e.target.value }))}
              fullWidth
              required
              slotProps={{ htmlInput: { min: 0 } }}
              helperText={
                logForm.eggs_laid
                  ? `Lay rate: ${((parseInt(logForm.eggs_laid, 10) / group.bird_count) * 100).toFixed(1)}%`
                  : `Target: ${group.bird_count} birds`
              }
            />
            <TextField
              label="Droppings Quality"
              select
              value={logForm.droppings_quality}
              onChange={(e) => setLogForm((f) => ({ ...f, droppings_quality: e.target.value }))}
              fullWidth
            >
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="loose">Loose (acceptable)</MenuItem>
              <MenuItem value="watery">Watery (warning)</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={logForm.feed_refused}
                  onChange={(e) => setLogForm((f) => ({ ...f, feed_refused: e.target.checked }))}
                  size="small"
                  color="warning"
                />
              }
              label={<Typography variant="body2">Feed refusal observed</Typography>}
            />
            <TextField
              label="Notes (optional)"
              multiline
              rows={2}
              value={logForm.notes}
              onChange={(e) => setLogForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
            />
            {group.baseline_lay_rate && logForm.eggs_laid && (
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: 'rgba(255,152,0,0.08)',
                  border: '1px solid rgba(255,152,0,0.2)',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Today vs baseline
                </Typography>
                {(() => {
                  const todayRate = (parseInt(logForm.eggs_laid, 10) / group.bird_count) * 100;
                  const diff = group.baseline_lay_rate - todayRate;
                  const color = diff >= 8 ? '#f44336' : diff >= 5 ? '#ff9800' : '#4caf50';
                  return (
                    <Typography variant="body2" sx={{ fontWeight: 700, color }}>
                      {diff > 0
                        ? `−${diff.toFixed(1)}% from baseline`
                        : `+${Math.abs(diff).toFixed(1)}% vs baseline`}
                      {diff >= 8
                        ? ' ⚠ HOLD recommended'
                        : diff >= 5
                          ? ' ⚠ Monitor closely'
                          : ' ✓ OK'}
                    </Typography>
                  );
                })()}
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleLog}
            disabled={!logForm.eggs_laid || logMutation.isPending}
            sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}
          >
            {logMutation.isPending ? <CircularProgress size={20} /> : 'Save Log'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Baseline Dialog */}
      <Dialog
        open={editBaselineOpen}
        onClose={() => setEditBaselineOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Set Baseline Lay Rate — {group.name}</DialogTitle>
        <DialogContent>
          <TextField
            label="Pre-switch baseline lay rate (%)"
            type="number"
            value={baselineForm}
            onChange={(e) => setBaselineForm(e.target.value)}
            fullWidth
            sx={{ mt: 1.5 }}
            slotProps={{ htmlInput: { min: 0, max: 100, step: 0.1 } }}
            helperText="Record today's lay rate as the pre-switch baseline"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBaselineOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveBaseline}
            disabled={!baselineForm || updateGroupMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      {historyOpen && (
        <GroupLogsDialog
          groupId={group.id}
          groupName={group.name}
          birdCount={group.bird_count}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </Paper>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

const FeedSwitchCampaignPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: campaigns = [], isLoading } = useSwitchCampaigns();
  const createCampaignMutation = useCreateSwitchCampaign();
  const deleteCampaignMutation = useDeleteSwitchCampaign();
  const addGroupMutation = useAddSwitchGroup();
  const { data: feedBrands = [] } = useFeedBrands();
  const { data: feedTypes = [] } = useFeedTypes();
  const { data: birdProcurementsPage } = useBirdProcurements({ per_page: 100 });

  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [alertsExpanded, setAlertsExpanded] = useState(true);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const birdProcurements = (birdProcurementsPage as any)?.data ?? [];

  const [campaignForm, setCampaignForm] = useState({
    title: '',
    from_feed_brand_id: null as number | null,
    from_feed_type_id: null as number | null,
    to_feed_brand_id: null as number | null,
    to_feed_type_id: null as number | null,
    bird_procurement_id: null as number | null,
    started_at: format(new Date(), 'yyyy-MM-dd'),
    notes: '',
  });

  const isStartCampaignButtonDisabled = useMemo(() => {
    return (
      Object.keys(campaignForm)
        .filter((key) => key !== 'notes' && key !== 'title')
        .some((key) => {
          const value = (campaignForm as any)[key];
          return value === null || value === '';
        }) || createCampaignMutation.isPending
    );
  }, [campaignForm, createCampaignMutation.isPending]);

  const [groupForm, setGroupForm] = useState({
    name: '',
    bird_count: '300',
    switch_start_date: format(new Date(), 'yyyy-MM-dd'),
    baseline_lay_rate: '',
  });

  const allCampaigns = campaigns as any[];
  const displayCampaign = selectedCampaignId
    ? allCampaigns.find((c) => c.id === selectedCampaignId)
    : (allCampaigns[0] ?? null);

  const alertGroups = (displayCampaign?.groups ?? []).filter(
    (g: any) => g.alert_status === 'warn' || g.alert_status === 'critical'
  );

  const feedLabel = (brandId: number | null, typeId: number | null) => {
    const brand = (feedBrands as any[]).find((b: any) => b.id === brandId)?.name ?? '';
    const type = (feedTypes as any[]).find((t: any) => t.id === typeId)?.name ?? '';
    return [brand, type].filter(Boolean).join(' ');
  };

  const handleCreateCampaign = async () => {
    try {
      const fromLabel = feedLabel(campaignForm.from_feed_brand_id, campaignForm.from_feed_type_id);
      const toLabel = feedLabel(campaignForm.to_feed_brand_id, campaignForm.to_feed_type_id);
      const payload = {
        ...campaignForm,
        title:
          campaignForm.title ||
          (fromLabel && toLabel ? `${fromLabel} → ${toLabel}` : 'Feed Switch'),
        from_feed: fromLabel,
        to_feed: toLabel,
      };
      const created = await createCampaignMutation.mutateAsync(payload);
      setCreateCampaignOpen(false);
      setSelectedCampaignId(created.id);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!deleteConfirmId) return;
    await deleteCampaignMutation.mutateAsync(deleteConfirmId);
    if (selectedCampaignId === deleteConfirmId) setSelectedCampaignId(null);
    setDeleteConfirmId(null);
  };

  const saveGroup = () => {
    if (!displayCampaign) return Promise.resolve();
    return addGroupMutation.mutateAsync({
      campaignId: displayCampaign.id,
      data: {
        ...groupForm,
        bird_count: parseInt(groupForm.bird_count, 10),
        baseline_lay_rate: groupForm.baseline_lay_rate
          ? parseFloat(groupForm.baseline_lay_rate)
          : undefined,
      },
    });
  };

  const handleAddGroup = async () => {
    try {
      await saveGroup();
      setAddGroupOpen(false);
      setGroupForm({
        name: `Group ${(displayCampaign?.groups?.length ?? 0) + 2}`,
        bird_count: '300',
        switch_start_date: format(new Date(), 'yyyy-MM-dd'),
        baseline_lay_rate: '',
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddGroupAndAnother = async () => {
    try {
      await saveGroup();
      setGroupForm({
        name: `Group ${(displayCampaign?.groups?.length ?? 0) + 2}`,
        bird_count: '300',
        switch_start_date: format(new Date(), 'yyyy-MM-dd'),
        baseline_lay_rate: '',
      });
    } catch (e) {
      console.error(e);
    }
  };

  const summary = displayCampaign?.groups_summary ?? {};

  return (
    <Box sx={{ p: 3 }}>
      {isLoading && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />
      )}

      {/* Page header */}
      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwitchIcon sx={{ color: '#ff9800' }} />
          <Typography variant="h5" fontWeight={700}>
            Feed Switch Campaigns
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ['feed', 'switch-campaigns'] })
              }
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateCampaignOpen(true)}
            sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}
          >
            New Campaign
          </Button>
        </Box>
      </Box>

      {/* Campaign list */}
      {!isLoading && allCampaigns.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <SwitchIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No feed switch campaigns yet
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Create a campaign to start the 7-day blended transition protocol
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateCampaignOpen(true)}
            sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}
          >
            New Campaign
          </Button>
        </Paper>
      )}

      {allCampaigns.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {allCampaigns.map((c: any) => {
            const isSelected = (selectedCampaignId ?? allCampaigns[0]?.id) === c.id;
            const hasAlerts = (c.groups ?? []).some(
              (g: any) => g.alert_status === 'warn' || g.alert_status === 'critical'
            );
            return (
              <Paper
                key={c.id}
                onClick={() => setSelectedCampaignId(c.id)}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  border: `1px solid ${isSelected ? '#ff9800' : 'rgba(255,255,255,0.1)'}`,
                  bgcolor: isSelected ? 'rgba(255,152,0,0.08)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  minWidth: 220,
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: '#ff9800', bgcolor: 'rgba(255,152,0,0.05)' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    noWrap
                    sx={{ color: isSelected ? '#ff9800' : 'inherit' }}
                  >
                    {c.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(c.started_at), 'dd MMM yyyy')} {c.groups_summary?.total ?? 0}{' '}
                    groups
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {hasAlerts && <WarningIcon sx={{ fontSize: 16, color: '#ff9800' }} />}
                  <Chip
                    label={c.status}
                    size="small"
                    color={c.status === 'active' ? 'success' : 'default'}
                    sx={{ fontSize: '0.65rem', height: 20, textTransform: 'capitalize' }}
                  />
                  <Tooltip title="Delete campaign">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmId(c.id);
                      }}
                      sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#f44336' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {displayCampaign && (
        <>
          {/* Campaign header card */}
          <Paper sx={{ p: 2.5, mb: 3, border: '1px solid rgba(255,152,0,0.2)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="h6" fontWeight={700}>
                  {displayCampaign.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Started {format(new Date(displayCampaign.started_at), 'dd MMM yyyy')}
                  {displayCampaign.bird_type ? ` · ${displayCampaign.bird_type}` : ''}
                </Typography>
                <Box
                  sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                >
                  {(displayCampaign.from_feed_label || displayCampaign.from_feed) && (
                    <Chip
                      label={`${displayCampaign.from_feed_label ?? displayCampaign.from_feed} → ${displayCampaign.to_feed_label ?? displayCampaign.to_feed}`}
                      size="small"
                      sx={{ bgcolor: 'rgba(255,152,0,0.1)', color: '#ff9800', fontSize: '0.7rem' }}
                    />
                  )}
                  {displayCampaign.bird_procurement && (
                    <Chip
                      label={`Batch: ${displayCampaign.bird_procurement.identifier} · ${(displayCampaign.bird_procurement.total_quantity ?? 0).toLocaleString()} birds`}
                      size="small"
                      sx={{ bgcolor: 'rgba(66,165,245,0.1)', color: '#42a5f5', fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {[
                  {
                    label: 'Total Birds',
                    value: summary.total_birds?.toLocaleString() ?? 0,
                    color: '#ff9800',
                  },
                  { label: 'In Progress', value: summary.in_progress ?? 0, color: '#42a5f5' },
                  { label: 'On Hold', value: summary.on_hold ?? 0, color: '#ff9800' },
                  { label: 'Completed', value: summary.completed ?? 0, color: '#4caf50' },
                ].map((s) => (
                  <Box key={s.label} sx={{ textAlign: 'center', minWidth: 60 }}>
                    <Typography variant="h5" fontWeight={700} sx={{ color: s.color }}>
                      {s.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Chip
                label={displayCampaign.status.replace('_', ' ')}
                size="small"
                color={displayCampaign.status === 'active' ? 'success' : 'default'}
                sx={{ textTransform: 'capitalize', fontWeight: 700 }}
              />
            </Box>
          </Paper>

          {/* 7-day blending reference */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'rgba(255,152,0,0.04)',
              border: '1px solid rgba(255,152,0,0.12)',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              7-DAY BLENDING PROTOCOL (per 300-bird group)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { days: 'Days 1–2', old: 75, new: 25 },
                { days: 'Days 3–4', old: 50, new: 50 },
                { days: 'Days 5–6', old: 25, new: 75 },
                { days: 'Day 7+', old: 0, new: 100 },
              ].map((s) => (
                <Box
                  key={s.days}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    border: '1px solid rgba(255,152,0,0.25)',
                    borderRadius: 1,
                    textAlign: 'center',
                    minWidth: 110,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block">
                    {s.days}
                  </Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#ff9800' }}>
                    {s.old > 0 ? `${s.old}% / ${s.new}%` : '100% Maridav'}
                  </Typography>
                </Box>
              ))}
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 1,
                  minWidth: 200,
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block">
                  Monitoring thresholds
                </Typography>
                <Typography variant="caption" sx={{ color: '#ff9800' }}>
                  ⚠ {'>'}5% lay drop = flag
                </Typography>
                <Typography variant="caption" sx={{ color: '#f44336', display: 'block' }}>
                  ✕ {'>'}8% after Day 7 = hold at 50/50
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Alert panel */}
          {alertGroups.length > 0 && (
            <Paper sx={{ mb: 3, border: '1px solid rgba(244,67,54,0.3)', overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  bgcolor: 'rgba(244,67,54,0.06)',
                }}
                onClick={() => setAlertsExpanded((v) => !v)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon sx={{ color: '#f44336' }} />
                  <Typography fontWeight={700} sx={{ color: '#f44336' }}>
                    {alertGroups.length} group{alertGroups.length > 1 ? 's' : ''} need attention
                  </Typography>
                </Box>
                <IconButton size="small">
                  {alertsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={alertsExpanded}>
                {alertGroups.map((g: any) => (
                  <Box
                    key={g.id}
                    sx={{
                      px: 2,
                      py: 1,
                      borderTop: '1px solid rgba(255,255,255,0.06)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 100 }}>
                      {g.name}
                    </Typography>
                    <Chip
                      size="small"
                      icon={alertIcon(g.alert_status) as React.ReactElement}
                      label={g.alert_status === 'critical' ? 'Hold at 50/50' : 'Lay drop >5%'}
                      sx={{
                        bgcolor: `${alertColor(g.alert_status)}22`,
                        color: alertColor(g.alert_status),
                      }}
                    />
                    {g.lay_rate_drop != null && (
                      <Typography variant="body2" sx={{ color: alertColor(g.alert_status) }}>
                        −{g.lay_rate_drop.toFixed(1)}% vs baseline
                      </Typography>
                    )}
                    {g.hold_reason && (
                      <Typography variant="caption" color="text.secondary">
                        {g.hold_reason}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Collapse>
            </Paper>
          )}

          {/* Groups grid */}
          {displayCampaign.groups?.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No groups added yet. Add the first 300-bird group to begin the transition.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setGroupForm({
                    name: 'Group 1',
                    bird_count: '300',
                    switch_start_date: format(new Date(), 'yyyy-MM-dd'),
                    baseline_lay_rate: '',
                  });
                  setAddGroupOpen(true);
                }}
                sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}
              >
                Add Group 1
              </Button>
            </Paper>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setGroupForm({
                      name: `Group ${(displayCampaign.groups?.length ?? 0) + 1}`,
                      bird_count: '300',
                      switch_start_date: format(new Date(), 'yyyy-MM-dd'),
                      baseline_lay_rate: '',
                    });
                    setAddGroupOpen(true);
                  }}
                  sx={{ borderColor: 'rgba(255,152,0,0.5)', color: '#ff9800' }}
                >
                  Add Group
                </Button>
              </Box>
              <Grid container spacing={2}>
                {(displayCampaign.groups ?? []).map((group: any) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={group.id}>
                    <GroupCard
                      group={group}
                      campaignId={displayCampaign.id}
                      fromFeed={displayCampaign.from_feed_label ?? displayCampaign.from_feed ?? ''}
                      toFeed={displayCampaign.to_feed_label ?? displayCampaign.to_feed ?? ''}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}

      {/* Create Campaign Dialog */}
      <Dialog
        open={createCampaignOpen}
        onClose={() => setCreateCampaignOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Start Feed Switch Campaign</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
            {/* From Feed */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: -1 }}>
              FROM FEED (current feed being replaced)
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Brand"
                  select
                  value={campaignForm.from_feed_brand_id ?? ''}
                  onChange={(e) =>
                    setCampaignForm((f) => ({
                      ...f,
                      from_feed_brand_id: e.target.value ? parseInt(e.target.value, 10) : null,
                    }))
                  }
                  fullWidth
                >
                  <MenuItem value="">— None —</MenuItem>
                  {(feedBrands as any[]).map((b: any) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Type"
                  select
                  value={campaignForm.from_feed_type_id ?? ''}
                  onChange={(e) =>
                    setCampaignForm((f) => ({
                      ...f,
                      from_feed_type_id: e.target.value ? parseInt(e.target.value, 10) : null,
                    }))
                  }
                  fullWidth
                >
                  <MenuItem value="">— None —</MenuItem>
                  {(feedTypes as any[]).map((t: any) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* To Feed */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: -1 }}>
              TO FEED (new feed being introduced)
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Brand"
                  select
                  value={campaignForm.to_feed_brand_id ?? ''}
                  onChange={(e) =>
                    setCampaignForm((f) => ({
                      ...f,
                      to_feed_brand_id: e.target.value ? parseInt(e.target.value, 10) : null,
                    }))
                  }
                  fullWidth
                >
                  <MenuItem value="">— None —</MenuItem>
                  {(feedBrands as any[]).map((b: any) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Type"
                  select
                  value={campaignForm.to_feed_type_id ?? ''}
                  onChange={(e) =>
                    setCampaignForm((f) => ({
                      ...f,
                      to_feed_type_id: e.target.value ? parseInt(e.target.value, 10) : null,
                    }))
                  }
                  fullWidth
                >
                  <MenuItem value="">— None —</MenuItem>
                  {(feedTypes as any[]).map((t: any) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Divider />

            <TextField
              label="Bird Procurement / Batch"
              select
              value={campaignForm.bird_procurement_id ?? ''}
              onChange={(e) =>
                setCampaignForm((f) => ({
                  ...f,
                  bird_procurement_id: e.target.value ? parseInt(e.target.value, 10) : null,
                }))
              }
              fullWidth
              helperText="Which bird batch is this switch being applied to?"
            >
              <MenuItem value="">— None —</MenuItem>
              {(birdProcurements as any[]).map((p: any) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.identifier}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Campaign Title (optional — auto-filled from feed names)"
              value={campaignForm.title}
              onChange={(e) => setCampaignForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              placeholder={`${feedLabel(campaignForm.from_feed_brand_id, campaignForm.from_feed_type_id) || 'Current feed'} → ${feedLabel(campaignForm.to_feed_brand_id, campaignForm.to_feed_type_id) || 'New feed'}`}
            />
            <TextField
              label="Campaign Start Date"
              type="date"
              value={campaignForm.started_at}
              onChange={(e) => setCampaignForm((f) => ({ ...f, started_at: e.target.value }))}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              required
            />
            <TextField
              label="Notes (optional)"
              multiline
              rows={2}
              value={campaignForm.notes}
              onChange={(e) => setCampaignForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCampaignOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateCampaign}
            disabled={isStartCampaignButtonDisabled}
            sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}
          >
            {createCampaignMutation.isPending ? <CircularProgress size={20} /> : 'Start Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Group Dialog */}
      <Dialog open={addGroupOpen} onClose={() => setAddGroupOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Group</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
            <TextField
              label="Group Name"
              value={groupForm.name}
              onChange={(e) => setGroupForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              required
              placeholder="e.g. Group 1"
            />
            <TextField
              label="Bird Count"
              type="number"
              value={groupForm.bird_count}
              onChange={(e) => setGroupForm((f) => ({ ...f, bird_count: e.target.value }))}
              fullWidth
              required
              slotProps={{ htmlInput: { min: 1 } }}
            />
            <TextField
              label="Switch Start Date"
              type="date"
              value={groupForm.switch_start_date}
              onChange={(e) => setGroupForm((f) => ({ ...f, switch_start_date: e.target.value }))}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              required
              helperText="Day 1 of the 7-day blending protocol for this group"
            />
            <TextField
              label="Baseline Lay Rate % (optional)"
              type="number"
              value={groupForm.baseline_lay_rate}
              onChange={(e) => setGroupForm((f) => ({ ...f, baseline_lay_rate: e.target.value }))}
              fullWidth
              slotProps={{ htmlInput: { min: 0, max: 100, step: 0.1 } }}
              helperText="Pre-switch lay rate to track drops against"
            />
            <Divider />
            <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,152,0,0.06)' }}>
              <Typography variant="caption" color="text.secondary">
                Stagger groups 3–4 days apart so no two groups are in peak disruption (Days 3–5) at
                the same time.
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddGroupOpen(false)}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={handleAddGroupAndAnother}
            disabled={!groupForm.name || !groupForm.bird_count || addGroupMutation.isPending}
            sx={{ borderColor: 'rgba(255,152,0,0.5)', color: '#ff9800' }}
          >
            Save & Add Another
          </Button>
          <Button
            variant="contained"
            onClick={handleAddGroup}
            disabled={!groupForm.name || !groupForm.bird_count || addGroupMutation.isPending}
            sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}
          >
            {addGroupMutation.isPending ? <CircularProgress size={20} /> : 'Add Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Campaign Confirmation */}
      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Campaign?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete the campaign and all its groups and daily logs. This cannot
            be undone.
          </Typography>
          {deleteConfirmId && (
            <Typography variant="body2" sx={{ mt: 1, color: '#ff9800', fontWeight: 600 }}>
              {allCampaigns.find((c: any) => c.id === deleteConfirmId)?.title}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteCampaign}
            disabled={deleteCampaignMutation.isPending}
          >
            {deleteCampaignMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedSwitchCampaignPage;
